import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faTriangleExclamation,
  faChartLine,
  faCircleInfo,
  faCaretUp,
  faCaretDown,
} from "@fortawesome/free-solid-svg-icons";
import { fmtMoney, fmtPct } from "../../utils/inventory/receiverMfcaUtils";
import "../../style/mfca-dashboard.css";

// optional period-over-period deltas — backend may or may not emit them. when
// absent, the card renders without the delta and reads correctly. flagging the
// "is-good" sign is direction-dependent: more reclaim is good (positive delta),
// less loss is good (negative delta).
const Delta = ({ value, betterWhenNegative = false }) => {
  if (value == null || value === 0) return null;
  const isGood = betterWhenNegative ? value < 0 : value > 0;
  return (
    <span className={`rl-kpi-delta ${isGood ? "is-good" : "is-bad"}`}>
      <FontAwesomeIcon icon={value < 0 ? faCaretDown : faCaretUp} />
      {value > 0 ? "+" : ""}{fmtPct(value)}
    </span>
  );
};

Delta.propTypes = {
  value: PropTypes.number,
  betterWhenNegative: PropTypes.bool,
};

const InfoIcon = ({ tip }) => (
  <Tooltip title={tip} arrow placement="top">
    <span className="rl-kpi-info-trigger" role="img" aria-label="info">
      <FontAwesomeIcon icon={faCircleInfo} />
    </span>
  </Tooltip>
);

InfoIcon.propTypes = { tip: PropTypes.string.isRequired };

const KpiStrip = ({ counters, deltas = {}, loading = false }) => {
  const { t } = useTranslation();

  const valueReclaimed = counters?.valueReclaimed || 0;
  const notUtilizedTotal = counters?.notUtilized?.total || 0;
  const utilizationRate = counters?.utilizationRate || 0;

  // hide the strip until first paint of data; loading paints a placeholder
  // shimmer that fits the same 138px-min layout so the page doesn't jump.
  if (loading && !counters) {
    return (
      <div className="rl-kpi-row">
        <div className="rl-kpi rl-kpi--good rl-kpi--loading" />
        <div className="rl-kpi rl-kpi--attention rl-kpi--loading" />
        <div className="rl-kpi rl-kpi--info rl-kpi--loading" />
      </div>
    );
  }

  return (
    <div className="rl-kpi-row">
      <div className="rl-kpi rl-kpi--good">
        <div className="rl-kpi-top">
          <span className="rl-kpi-icon">
            <FontAwesomeIcon icon={faBoxOpen} />
          </span>
          <div className="rl-kpi-value-row">
            <span className="rl-kpi-value">{fmtMoney(valueReclaimed)}</span>
            <Delta value={deltas.valueReclaimed} />
          </div>
        </div>
        <div className="rl-kpi-bottom">
          <span className="rl-kpi-label">
            {t("Value Reclaimed")}
            <InfoIcon
              tip={t("Estimated savings vs. buying these materials new. Based on reference prices where available, falling back to listing cost or your acquisition cost. This is an estimate, not accounting-grade.")}
            />
          </span>
          <span className="rl-kpi-subline">{t("estimated savings vs. buying it new")}</span>
        </div>
      </div>

      <div className="rl-kpi rl-kpi--attention">
        <div className="rl-kpi-top">
          <span className="rl-kpi-icon">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </span>
          <div className="rl-kpi-value-row">
            <span className="rl-kpi-value">{fmtMoney(notUtilizedTotal)}</span>
            <Delta value={deltas.notUtilized} betterWhenNegative />
          </div>
        </div>
        <div className="rl-kpi-bottom">
          <span className="rl-kpi-label">
            {t("Not Utilized")}
            <InfoIcon
              tip={t("Dollar value of rejected material in this period, split by what happened to it: disposal loss (you disposed of it locally) or quality issue (returned to donor or credited).")}
            />
          </span>
          <span className="rl-kpi-subline">{t("dollar value of rejected material")}</span>
        </div>
      </div>

      <div className="rl-kpi rl-kpi--info">
        <div className="rl-kpi-top">
          <span className="rl-kpi-icon">
            <FontAwesomeIcon icon={faChartLine} />
          </span>
          <div className="rl-kpi-value-row">
            <span className="rl-kpi-value">{fmtPct(utilizationRate)}</span>
            <Delta value={deltas.utilizationRate} />
          </div>
        </div>
        <div className="rl-kpi-bottom">
          <span className="rl-kpi-label">
            {t("Utilization Rate")}
            <InfoIcon
              tip={t("Share of delivered material you accepted. Read this alongside the Donor Breakdown below — a single number tells you what happened, the donor breakdown tells you where it came from.")}
            />
          </span>
          <span className="rl-kpi-subline">{t("share of delivered material you accepted")}</span>
        </div>
      </div>
    </div>
  );
};

KpiStrip.propTypes = {
  counters: PropTypes.shape({
    valueReclaimed: PropTypes.number,
    notUtilized: PropTypes.shape({
      total: PropTypes.number,
    }),
    utilizationRate: PropTypes.number,
  }),
  deltas: PropTypes.shape({
    valueReclaimed: PropTypes.number,
    notUtilized: PropTypes.number,
    utilizationRate: PropTypes.number,
  }),
  loading: PropTypes.bool,
};

export default KpiStrip;
